/**
 * Maps an unknown thrown value to root-boundary user copy (code → heuristics → default).
 */

import { isAppError } from "@/lib/errors/app-error";
import {
  ROOT_ERROR_CATALOG,
  SUPPORT_CONTACT_FOOTER,
  type RootErrorCatalogEntry,
  type RootErrorCatalogKey,
} from "@/lib/errors/root-error-catalog";

export type ResolvedRootError = RootErrorCatalogEntry & {
  catalogKey: RootErrorCatalogKey;
  technicalDetail: string;
  supportFooter: string;
};

function catalogEntry(key: RootErrorCatalogKey): ResolvedRootError {
  const entry = ROOT_ERROR_CATALOG[key];
  return {
    catalogKey: key,
    title: entry.title,
    message: entry.message,
    technicalDetail: "",
    supportFooter: SUPPORT_CONTACT_FOOTER,
  };
}

function technicalDetailFrom(error: unknown): string {
  if (!(error instanceof Error)) {
    return String(error);
  }

  const parts: string[] = [];
  if (isAppError(error)) {
    if (error.code) parts.push(`code: ${error.code}`);
    if (error.status != null) parts.push(`status: ${error.status}`);
  }
  parts.push(error.message);
  if (error.stack) {
    parts.push(error.stack);
  }
  return parts.join("\n\n");
}

function resolveByCode(code: string): RootErrorCatalogKey | null {
  if (Object.hasOwn(ROOT_ERROR_CATALOG, code)) {
    return code as RootErrorCatalogKey;
  }
  return null;
}

function resolveByStatus(
  status: number | undefined
): RootErrorCatalogKey | null {
  if (status === undefined) return null;
  if (status === 404) return "not_found";
  if (status === 503) return "api_error";
  if (status >= 500 && status <= 504) return "api_error";
  return null;
}

function resolveByMessage(message: string): RootErrorCatalogKey | null {
  const lower = message.toLowerCase();
  if (
    lower.includes("shared conversation not found") ||
    lower.includes("conversation indisponible")
  ) {
    return "shared_conversation_unavailable";
  }
  if (
    lower.includes("conversation not found") ||
    lower.includes("conversation introuvable")
  ) {
    return "conversation_not_found";
  }
  if (
    lower.includes("failed to fetch") ||
    lower.includes("networkerror") ||
    lower.includes("load failed")
  ) {
    return "network_error";
  }
  const httpMatch = /HTTP(?:\s+error!)?\s*status:\s*(\d{3})/i.exec(message);
  if (httpMatch) {
    return resolveByStatus(Number(httpMatch[1]));
  }
  if (lower.includes("not found")) {
    return "not_found";
  }
  return null;
}

export function resolveRootError(error: unknown): ResolvedRootError {
  const technicalDetail = technicalDetailFrom(error);
  let key: RootErrorCatalogKey = "default";
  let matchedExplicitCode = false;

  if (isAppError(error)) {
    if (error.code) {
      const byCode = resolveByCode(error.code);
      if (byCode !== null) {
        key = byCode;
        matchedExplicitCode = true;
      }
    }
    // Status heuristic only when no catalog code was provided (or code unknown).
    if (!matchedExplicitCode) {
      const byStatus = resolveByStatus(error.status);
      if (byStatus) key = byStatus;
    }
  }

  if (!matchedExplicitCode && key === "default" && error instanceof Error) {
    const byMessage = resolveByMessage(error.message);
    if (byMessage) key = byMessage;
  }

  if (!matchedExplicitCode && key === "default" && error instanceof TypeError) {
    key = "network_error";
  }

  return {
    ...catalogEntry(key),
    technicalDetail,
  };
}
